import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useParams,
} from "react-router-dom";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { each, findWhere } from "underscore";
import { useEffect, useMemo } from "react";
import { Container } from "reactstrap";
import Cookies from "js-cookie";

import {
  addUserModal,
  cloudLogoAtom,
  maintenanceModeAtom,
  queriesEnabledAtom,
  restoringBackupAtom,
  setSchemaAtom,
  tenantAtom,
} from "../atoms/GlobalAtoms";
import useUserRecordActivity from "../components/Records/References/ReferencesComponents/SelectableReferences/useRecordActivity";
import ManageModuleIndexes from "../views/supportal/CloudDetailView/ModuleIndexes/ManageModuleIndexes";
import CRMModuleFieldSettingsManageView from "../views/modules/CRMModuleFieldSettingsManageView";
import CRMModuleFieldSettingsView from "../views/modules/CRMModuleFieldSettingsView";
import NotificationsHome from "../views/cloud/Notifications/NotificationsHome";
import GrowthCloudStore from "../components/GrowthCloudStore/GrowthCloudStore";
import BrieChatMenuButton from "../components/BrieChatMenu/BrieChatMenuButton";
import { OnBoardModal } from "../views/cloud/CreateCloudView/CreateCloudView";
import RestoringBackupModal from "../components/Modals/RestoringBackupModal";
import WorkspaceActivity from "../views/workspaceActivity/WorkspaceActivity";
import RelatedRecords from "../views/cloud/RelatedRecords/RelatedRecords";
import SecurityAndAccess from "../views/cloud/security/SecurityAndAccess";
import CRMAPIKeySettingsView from "../views/cloud/CRMAPIKeySettingsView";
import WorkflowEditorView from "../views/workflows/WorkflowEditorView";
import MaintenanceAlert from "../components/Other/MaintenanceAlert";
import CRMRecordListView from "../views/records/CRMRecordListView";
import { AccountTags } from "../views/cloud/account/AccountTags";
import { handleLocalNotifications } from "../utils/GeneralUtils";
import CloudSettingsHome from "../views/cloud/CloudSettingsHome";
import PublicForms from "../views/cloud/PublicForms/PublicForms";
import RecordDetailView from "../views/records/RecordDetailView";
import BulkEmailsHome from "../views/bulkEmails/BulkEmailsHome";
import useCloudPermissions from "../hooks/useCloudPermissions";
import AllWorkspaces from "../views/workspaces/AllWorkspaces";
import UserActivity from "../views/userActivity/UserActivity";
import AppHelmet from "../components/AppComponents/AppHelmet";
import { CrashViewComponent } from "../views/other/CrashView";
import CloudBackup from "../views/cloud/Backups/CloudBackup";
import DeletedRecords from "./DeletedRecords/DeletedRecords";

import CloudModuleSchema from "../views/supportal/CloudDetailView/TenantSchema/CloudModuleSchemaView";
import ManageCloudApp from "../views/supportal/CloudDetailView/CloudApps/ManageCloudApp";
import CloudListView from "../views/supportal/CloudListView/CloudListView";
import SupportalUsers from "../views/supportal/Users/SupportalUsers";
import ActivityLog from "../views/supportal/ActivityLog/ActivityLog";
import PersonDetailView from "../views/supportal/PersonDetailView";
import WorkflowHomeView from "../views/workflows/WorkflowHomeView";
import SupportalCloudsLayout from "./SupportalCloudsLayout";
import AdminFooter from "../components/Footers/AdminFooter";
import WorkspaceContext from "../context/WorkspaceContext";
import MissionControlLayout from "./MissionControlLayout";
import SharingCenter from "./SharingCenter/SharingCenter";
import { Sidebar } from "../components/Sidebar/Sidebar";
import CRMNavbar from "../components/Navbars/CRMNavbar";
import useCloud from "../api/api-hooks/cloud/useCloud";
import useSocketEvents from "../hooks/useSocketEvents";
import ManageTags from "../components/Tags/ManageTags";
import Maintenance from "../views/other/Maintenance";
import Globals from "../views/cloud/Globals/Globals";
import { getCurrentUser } from "../utils/UserUtils";
import HomeView from "../views/workspaces/HomeView";
import { newToastEvent } from "../utils/ToastUtil";
import { CRMRoutes } from "./routes/CRMRoutes";
import Library from "../views/library/Library";
import PageView from "../views/pages/PageView";
import TenantAPI from "../api/api/TenantAPI";
import AccountLayout from "./AccountLayout";
import Constants from "../utils/Constants";
import Brie from "../views/brie/Brie";

const ProtectedRoute = ({
  cPermissionProfile,
  cPermissions,
  scopes,
  element,
  tenantID,
}: any) => {
  const navigate = useNavigate();
  if (!cPermissionProfile) {
    return null;
  }

  for (const scope of scopes) {
    if (!cPermissions[scope]) {
      newToastEvent("error", "You don't have permission to access that page.");
      return navigate(`/clouds/${tenantID}/activity`);
    }
  }

  return element;
};

const CRMLayout = () => {
  const navigate = useNavigate();
  const [modalAtom, setModal] = useAtom(addUserModal);
  const setTenant = useSetAtom(tenantAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const setCloudLogo = useSetAtom(cloudLogoAtom);
  const restoringBackup = useAtomValue(restoringBackupAtom);
  const maintenanceMode = useAtomValue(maintenanceModeAtom);
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const { tenantID } = useParams();
  const queryClient = useQueryClient();
  const { cPermissions, cPermissionProfile } = useCloudPermissions();
  const { startSocketConnection, endSocketConnection } = useSocketEvents();
  const { cloud } = useCloud(tenantID ?? "");

  // Handle localStorage notifications
  handleLocalNotifications();

  TenantAPI.setCurrentTenant(tenantID!);

  // To Fetch the user record activity
  useUserRecordActivity();

  const handleRouteChange = () => {
    if (!Cookies.get("authenticationToken")) {
      const path = window.location.href;

      return navigate("/login?r=" + path);
    }

    getCurrentUser()
      .then((user) => {
        each(user?.Tenants, (t) => {
          if (tenantID === t.TenantID) {
            setTenant(t);
          }
        });

        if (!findWhere(user?.Tenants, { TenantID: tenantID })) {
          newToastEvent(
            "error",
            "You don't have permission to access that page. Select a cloud to continue."
          );

          return navigate("/clouds");
        }

        const tenantToken = Cookies.get(`authenticationToken_${tenantID}`);

        if (!tenantToken) {
          TenantAPI.getAuthenticationToken(tenantID!).then(
            (data) => {
              Cookies.set(
                `authenticationToken_${tenantID}`,
                data.data.data.authenticationToken,
                {
                  expires: 7,
                  sameSite: "strict",
                  secure: window.location.origin.startsWith("https"),
                }
              );

              TenantAPI.setCurrentTenant(tenantID!);

              setCloudLogo(data.data.data?.logo?.PublicURL ?? "");

              // Start socket connection
              startSocketConnection(data.data.data.authenticationToken);

              TenantAPI.getLatestSchema().then((schema) => {
                setSchema(schema);
              });
            },
            (error) => {
              console.error(error);

              newToastEvent(
                "error",
                "You don't have permission to access that page. Select a cloud to continue."
              );

              return navigate("/clouds");
            }
          );
        } else {
          TenantAPI.setCurrentTenant(tenantID!);

          // Start socket connection
          startSocketConnection(tenantToken);

          TenantAPI.getLatestSchema().then((schema) => {
            setSchema(schema);
          });
        }
      })
      .catch((e) => {
        console.log("Error in CRMLayout, redirecting to login", e);
        return navigate(`/login?r=${window.location.href}`);
      });
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement!.scrollTop = 0;
  }, [navigate]);

  useEffect(() => {
    if (!queriesEnabled) return;

    handleRouteChange();

    if (tenantID) {
      queryClient.invalidateQueries({
        queryKey: ["user", tenantID],
      });
    }

    return () => {
      endSocketConnection();
    };
  }, [tenantID, queriesEnabled]);

  const ProtectedRouteProps = {
    cPermissionProfile,
    cPermissions,
    tenantID,
  };

  if (maintenanceMode?.Status) {
    return <Maintenance maintenanceMode={maintenanceMode} />;
  }

  const links = useMemo(() => {
    return CRMRoutes(tenantID).items;
  }, [tenantID]);

  return (
    <>
      <AppHelmet title="Home - GrowthCloud.com" />
      <WorkspaceContext>
        <Sidebar requiresWorkspace={true} links={links as any}>
          <CRMNavbar isCloudSupportal={tenantID === Constants.SUP_CLOUD_ID} />
          <ErrorBoundary
            FallbackComponent={CrashViewComponent}
            resetKeys={[location.pathname, location.search]}
          >
            <BrieChatMenuButton />
            <GrowthCloudStore />
            {cloud && cloud?.Maintenance?.Status && <MaintenanceAlert />}
            <Routes>
              <Route
                path="workspaces/:workspaceID/home/:tab"
                element={<HomeView />}
              />
              <Route
                path="workspaces/:workspaceID/activity"
                element={<WorkspaceActivity />}
              />
              <Route path="activity" element={<UserActivity />} />
              <Route path="workspaces" element={<AllWorkspaces />} />
              <Route
                path="workspaces/:workspaceID/pages/:pageID/views/:viewID"
                element={<PageView />}
              />
              <Route
                path="records/:ModuleAPIName"
                element={<CRMRecordListView />}
              />
              <Route
                path="records/:ModuleAPIName/details/:recordID/:tab"
                element={<RecordDetailView />}
              />
              <Route
                path="pages/:pageID/views/:viewID/records/:ModuleAPIName/details/:recordID/:tab"
                element={<RecordDetailView />}
              />
              <Route path="settings/home" element={<CloudSettingsHome />} />
              <Route
                path="settings/tags"
                element={
                  <>
                    <AppHelmet title="Cloud Tags - GrowthCloud.com" />
                    <Container fluid className="mt-4">
                      <ManageTags
                        title="Cloud Tags"
                        scope={{ Type: "Cloud" }}
                        description="Create and manage tags available across your cloud. Cloud tags can be applied to records across all modules."
                      />
                    </Container>
                  </>
                }
              />
              <Route path="settings/account/:tab" element={<AccountLayout />} />
              <Route
                path="settings/sharing-center/:tab"
                element={<SharingCenter />}
              />
              <Route
                path="settings/recycle-bin"
                element={
                  <ProtectedRoute
                    scopes={["cloud_modify_deleted_records"]}
                    element={<DeletedRecords />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/modules"
                element={
                  <ProtectedRoute
                    scopes={["cloud_create_module"]}
                    element={<CRMModuleFieldSettingsView />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/modules/manage/:APIName/:tab"
                element={
                  <ProtectedRoute
                    scopes={["cloud_modify_module"]}
                    element={<CRMModuleFieldSettingsManageView />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/security-access/:tab"
                element={
                  <ProtectedRoute
                    scopes={["cloud_create_person"]}
                    element={<SecurityAndAccess />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/api-keys"
                element={
                  <ProtectedRoute
                    scopes={["cloud_get_credentials"]}
                    element={<CRMAPIKeySettingsView />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/related-records"
                element={
                  <ProtectedRoute
                    scopes={["cloud_get_credentials"]}
                    element={<RelatedRecords />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/backup/:tab"
                element={
                  <ProtectedRoute
                    scopes={["cloud_get_credentials"]}
                    element={<CloudBackup />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/publicForms"
                element={
                  <ProtectedRoute
                    scopes={["cloud_get_credentials"]}
                    element={<PublicForms />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route
                path="settings/globals/:tab"
                element={
                  <ProtectedRoute
                    scopes={["cloud_get_credentials"]}
                    element={<Globals />}
                    {...ProtectedRouteProps}
                  />
                }
              />
              <Route path="settings/tags/user" element={<AccountTags />} />
              <Route path="workflows" element={<WorkflowHomeView />} />
              <Route
                path="workflows/edit/:workflowID"
                element={<WorkflowEditorView />}
              />
              <Route path="emails" element={<BulkEmailsHome />} />
              <Route path="brie/:tab" element={<Brie />} />
              <Route
                path="gc-drive"
                element={<Library type="Growth Drive" />}
              />
              <Route path="user/:tab" element={<NotificationsHome />} />
              <Route
                path="supportal/users"
                element={
                  <SupportalUsers
                    urlStart={`/clouds/${Constants.SUP_CLOUD_ID}/supportal/users`}
                  />
                }
              />
              <Route
                path="supportal/users/:personID"
                element={
                  <PersonDetailView
                    urlStart={`/clouds/${Constants.SUP_CLOUD_ID}/supportal/users`}
                  />
                }
              />
              <Route path="supportal/activity-log" element={<ActivityLog />} />
              <Route
                path="supportal/clouds"
                element={
                  <CloudListView
                    urlStart={`/clouds/${Constants.SUP_CLOUD_ID}/supportal/clouds`}
                    mode="supp"
                  />
                }
              />

              <Route
                path="supportal/clouds/:cloudID/:tab"
                element={
                  <SupportalCloudsLayout
                    urlStart={`/clouds/${Constants.SUP_CLOUD_ID}/supportal/clouds`}
                    mode="supp"
                  />
                }
              />
              <Route
                path="supportal/clouds/:cloudID/cloudApps/:cloudAppID"
                element={<ManageCloudApp />}
              />
              <Route
                path="supportal/clouds/:cloudID/modules/:moduleAPIName"
                element={<CloudModuleSchema />}
              />
              <Route
                path="supportal/clouds/:cloudID/modules/:moduleAPIName/indexes"
                element={<ManageModuleIndexes isSupportal />}
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      Cookies.get("authenticationToken") && tenantID
                        ? `/clouds/${tenantID}/workspaces`
                        : "/login"
                    }
                    replace
                  />
                }
              />
              <Route path="mc/*" element={<MissionControlLayout />} />
            </Routes>
          </ErrorBoundary>
          <OnBoardModal
            tenantID={tenantID!}
            open={modalAtom}
            setModal={setModal}
          />
          <AdminFooter />
        </Sidebar>
      </WorkspaceContext>
      {!!restoringBackup && <RestoringBackupModal />}
    </>
  );
};

export default CRMLayout;
